<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { $, defineSchema } from '@bagelink/vue'
const schema = defineSchema({
	anchor: $.text('Section anchor'),
	eyebrow: $.text('Eyebrow'),
	title: $.text('Heading', { default: 'Heading' }),
	specialHeading: $.checkbox('Use special heading font', { default: true }),
	content: $.richtext('Content', { simple: true, default: '<p>Add your content here.</p>' }),
	columns: $.select('Columns', [
		{ label: 'One column', value: 'one' },
		{ label: 'Two columns', value: 'two' },
	], { default: 'one' }),
	secondaryContent: $.richtext('Second column content', {
		simple: true,
		default: '<p>Add the second column content here.</p>',
	}).if('columns eq "two"'),
	align: $.select('Alignment', [
		{ label: 'Start', value: 'start' },
		{ label: 'Center', value: 'center' },
	], { default: 'start' }),
	width: $.select('Content width', [
		{ label: 'Narrow', value: 'narrow' },
		{ label: 'Regular', value: 'regular' },
		{ label: 'Wide', value: 'wide' },
	], { default: 'regular' }),
	surface: $.select('Background', [
		{ label: 'Page background', value: 'page' },
		{ label: 'White surface', value: 'surface' },
		{ label: 'Brand color', value: 'brand' },
		{ label: 'Dark', value: 'dark' },
		{ label: 'Image', value: 'image' },
	], { default: 'page' }),
	backgroundImage: $.upload('Background image').if('surface eq "image"'),
	backgroundFit: $.select('Background image fit', [
		{ label: 'Cover', value: 'cover' },
		{ label: 'Contain', value: 'contain' },
		{ label: 'Fill', value: 'fill' },
	], { default: 'cover' }).if('surface eq "image" and backgroundImage pr'),
	dimBackground: $.checkbox('Dim background', { default: false }).if('surface eq "image" and backgroundImage pr'),
	card: $.checkbox('Show in card', { default: false }),
	collapsible: $.checkbox('Collapse long content', { default: false }),
	collapsedLabel: $.text('Expand button label', { default: 'Read more' }).if('collapsible eq true'),
	expandedLabel: $.text('Collapse button label', { default: 'Show less' }).if('collapsible eq true'),
	compact: $.checkbox('Compact spacing', { default: false }),
})
export const RichText = defineBlock({
	name: 'RichText',
	label: 'Rich text',
	icon: 'text_fields',
	thumbnail: '/block-thumbs/RichText.svg',
	category: 'General',
	order: 110,
	schema,
})
</script>
<script setup lang="ts">
import { computed, ref } from 'vue'

import { Btn, Card, pathKeyToURL, sanitizeHtml } from '@bagelink/vue'
const props = defineProps<{
	anchor?: string
	eyebrow?: string
	title?: string
	specialHeading?: boolean
	content?: string
	columns?: 'one' | 'two'
	secondaryContent?: string
	align?: 'start' | 'center'
	width?: 'narrow' | 'regular' | 'wide'
	surface?: 'page' | 'surface' | 'brand' | 'dark' | 'image'
	backgroundImage?: string
	backgroundFit?: 'cover' | 'contain' | 'fill'
	dimBackground?: boolean
	card?: boolean
	collapsible?: boolean
	collapsedLabel?: string
	expandedLabel?: string
	compact?: boolean
}>()

const expanded = ref(false)
const surfaceClass = computed(() => ({
	page: 'bg-bg',
	surface: 'bg-box',
	brand: 'bg-primary color-white keep-white',
	dark: 'bg-black color-white keep-white',
	image: 'color-white keep-white',
}[props.surface ?? 'page']))
const sectionStyle = computed(() => props.surface === 'image' && props.backgroundImage
	? {
			backgroundImage: `${props.dimBackground ? 'linear-gradient(#0006, #0006), ' : ''}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			backgroundSize: props.backgroundFit === 'fill' ? '100% 100%' : (props.backgroundFit ?? 'cover'),
		}
	: undefined)
const widthClass = computed(() => ({
	narrow: 'w-650px',
	regular: 'w-900px',
	wide: 'w-1200px',
}[props.width ?? 'regular']))
const contentClass = computed(() => props.columns === 'two'
	? 'grid grid-wrap-2 gap-2 m_gap-1 align-content-start'
	: '')
const hasContent = computed(() => Boolean(props.content || (props.columns === 'two' && props.secondaryContent)))
</script>

<template>
	<section
		:id="anchor || undefined"
		class="px-2 m_px-1"
		:class="[surfaceClass, compact ? 'py-2 m_py-1' : 'py-4 m_py-2']"
		:style="sectionStyle"
	>
		<Card
			:frame="card"
			:class="[
				widthClass,
				card ? '' : 'bg-transparent',
				align === 'center' ? 'txt-center' : 'txt-start',
			]"
		>
			<p v-if="eyebrow" class="m-0 pb-05 uppercase tracking-wide semi">{{ eyebrow }}</p>
			<h2
				v-if="title"
				class="m-0 txt-48 m_txt-32 line-height-11 m_line-height-12 balance pb-1"
				:class="{ 'blox-special-heading': specialHeading }"
			>
				{{ title }}
			</h2>
			<div
				v-if="hasContent"
				:class="collapsible && !expanded ? 'rich-text-collapse-fade max-h-200px overflow-hidden' : ''"
			>
				<div :class="contentClass">
					<!-- eslint-disable vue/no-v-html -- Rich text is sanitized before rendering. -->
					<div v-if="content" class="pretty" v-html="sanitizeHtml(content)" />
					<div
						v-if="columns === 'two' && secondaryContent"
						class="pretty"
						v-html="sanitizeHtml(secondaryContent)"
					/>
					<!-- eslint-enable vue/no-v-html -->
				</div>
			</div>
			<Btn
				v-if="collapsible && hasContent && (expanded ? expandedLabel : collapsedLabel)"
				flat
				class="mt-1"
				:icon-end="expanded ? 'expand_less' : 'expand_more'"
				:value="expanded ? expandedLabel : collapsedLabel"
				@click="expanded = !expanded"
			/>
		</Card>
	</section>
</template>

<style scoped>
.rich-text-collapse-fade {
	mask-image: linear-gradient(to bottom, black 65%, transparent);
}
</style>
